import React, { useState, useEffect, useRef } from 'react';
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { ButtonClose } from '@lk-gtcom/ecomlab-components';
import { Andrey3Https } from '../../fetchUrls';
import { ReactComponent as Send } from './img/bx-send.svg'
import chat_big_icon from './img/chat_big_icon.svg'
import dayjs from 'dayjs';
import './ChatWidget.scss';

const generateUUID4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const ChatWidget = ({ botId, handleKillChat, chatName }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const [sessionId] = useState(generateUUID4());
  const ws = useRef(null);
  const messageIds = useRef(new Set());

  const messagesEndRef = useRef(null);

  // Создание чата и подключение WebSocket
  useEffect(() => {
    if (botId && isChatOpen) {
      const apiUrl = `${Andrey3Https}/chat-bot/chat/${botId}/create?session_id=${sessionId}`;

      fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
        .then((res) => res.json())
        .then((data) => {
          const { chat, space_id, token } = data
          connectWebSocket(chat?.chat_id, token, space_id);
        })
        .catch((error) => console.error('Ошибка создания чата:', error));
    }

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [botId, sessionId, isChatOpen]);

  // Подключение WebSocket
  const connectWebSocket = (chatId, token, space) => {
    const wsUrl = `wss://apps0.ecomru.ru:4454/ws/chat_id/${chatId}?authorization=${token}&space_id=${space}`;

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);

      // Запрашиваем историю сообщений сразу после подключения
      ws.current.send(JSON.stringify({
        type: 'get_history',
        early_message_id: null,
        limit: 10,
        chat_id: chatId,
      }));
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleIncomingMessage(data);
      } catch (error) {
        console.error('Ошибка парсинга сообщения:', error);
      }
    };

    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    };

    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };
  };

  // Обработка входящих сообщений
  const handleIncomingMessage = (data) => {
    const { messages: msgList, signal } = data;

    // Пакет истории / подгрузка старых сообщений
    if (msgList) {
      const normalized = msgList
        .filter(msg => !messageIds.current.has(msg.message_id))
        .map(msg => {
          messageIds.current.add(msg.message_id);
          return {
            sender: msg.author?.id === botId ? 'bot' : 'user',
            content: msg.text,
            messageId: msg.message_id,
            timestamp: msg.created_at || new Date().toISOString(),
          };
        });

      // История приходит от старых к новым — добавляем в начало
      setMessages(prev => [...normalized, ...prev]);
      return;
    }

    // Сигнал удаления сообщения
    if (signal?.type === 'delete') {
      setMessages(prev => prev.filter(m => m.messageId !== signal.current_message_id));
      return;
    }

    // Сигнал обновления сообщения
    if (signal?.type === 'update') {
      setMessages(prev => prev.map(m =>
        m.messageId === signal.current_message_id
          ? { ...m, content: signal.message?.text ?? m.content }
          : m
      ));
      return;
    }

    // Новое входящее сообщение (от пользователя или бота)
    if (data.message_id && !messageIds.current.has(data.message_id)) {
      messageIds.current.add(data.message_id);

      const newMsg = {
        sender: data.author?.id === botId ? 'bot' : 'user',
        content: data.text,
        messageId: data.message_id,
        timestamp: data.created_at || new Date().toISOString(),
      };

      setMessages(prev => [...prev, newMsg]);
      setIsTyping(false);
    }
  };

  // Отправка сообщения
  const handleSendMessage = () => {
    if (!inputValue.trim() || !ws.current || !isConnected) return;

    const text = inputValue.trim();
    setInputValue('');
    setIsTyping(true);

    // Сообщение не добавляем локально — сервер пришлёт его обратно через onmessage
    try {
      ws.current.send(JSON.stringify({
        current_user: botId,
        message_text: text,
        parent_message_id: null,
        files: [],
      }));
    } catch (error) {
      console.error('Ошибка при отправке сообщения через WebSocket:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Закрытие WebSocket при размонтировании или закрытии чата
  useEffect(() => {
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  return (
    <div>
      <div
        className="widget-chat"
        onClick={() => {
          setIsChatOpen(!isChatOpen);
        }}
      >
        <img className='widget-chat__img' src={chat_big_icon} />
      </div>

      {isChatOpen && (
        <div className="chat-modal">
          <header className="chat-modal__header">
            <TitlePage
              medium
              title={<>Чат с ботом {chatName}</>}
              style={{ color: '#fff', fontSize: '16px' }}
            />
            <span className={`chat-modal__status ${isConnected ? 'chat-modal__status_green' : 'chat-modal__status_red'}`}></span>
            <ButtonClose
              s
              onClick={handleKillChat}
            />
          </header>

          <div className="chat-messages">
            {messages.map((message, index) => {
              return <div
                key={message.messageId || index}
                className={`chat-message ${message.sender === 'bot' ? 'bot-message' : 'user-message'}`}
              >
                <div className="message-content">{message.content}</div>
                <div className="chat-modal__time">
                  {dayjs(message.timestamp).format('HH:mm')}
                </div>
              </div>
            })}
            {isTyping && (
              <div className="chat-typing-indicator">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder="Введите сообщение..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={!isConnected}
            />

            <button
              className='chat-modal__btn'
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || !isConnected}
            >
              <Send
                className='chat-modal__icon'
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;